/**
 *  @class
 *  @function SearchForm
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.is_drawer =
        this.dataset.drawer;

      this.drawer =
        this.closest(
          '#Search-Drawer'
        );

      this.form =
        this.querySelector(
          'form'
        );

      this.button =
        document.querySelectorAll(
          '.thb-quick-search'
        );

      this.input =
        this.querySelector(
          '.search-field'
        );

      this.defaultTab =
        this.querySelector(
          '.side-panel-content--initial'
        );

      this.predictiveSearchResults =
        this.querySelector(
          '.thb-predictive-search'
        );

      this.cc =
        this.querySelector(
          '.searchform--click-capture'
        );

      this.emptyPredictiveMarkup =
        this.predictiveSearchResults
          ?.querySelector(
            '[data-ps-empty]'
          )
          ?.outerHTML || '';

      this.emptyPredictiveRequest =
        null;

      this.setupEventListeners();

      /*
       * Prevent stale renders.
       */
      this._lastTerm = '';

      this._fetchController =
        null;

      this._fullProductCountCache =
        this._fullProductCountCache ||
        new Map();

      this._fullProductCountController =
        null;

      /*
       * Prevent double-binding
       * document shields.
       */
      this._recentShieldBound =
        this._recentShieldBound ||
        false;

      /*
       * Cache XREF responses by
       * customer search term.
       */
      this._xrefCache =
        this._xrefCache ||
        new Map();

      this._xrefController =
        null;

      /*
       * Cache Shopify product
       * resolutions by Clarus SKU.
       */
      this._xrefProductCache =
        this._xrefProductCache ||
        new Map();

      /*
       * Reuse a Shopify predictive
       * result shell when possible.
       */
      this._xrefShellHtml =
        this._xrefShellHtml ||
        '';

      /*
       * Prevent an older compatibility
       * search from updating the UI.
       */
      this._xrefRenderToken =
        null;

      /*
       * Tracks whether the customer's
       * actual Shopify query matched
       * products.
       *
       * Recommended Products do not
       * count as a real match.
       */
      this._hasActualProductMatch =
        null;
    }


    setupEventListeners() {
      this.form?.addEventListener(
        'submit',
        this.onFormSubmit.bind(
          this
        )
      );

      /*
       * Input typing.
       */
      this.input?.addEventListener(
        'input',
        debounce(
          () => this.onChange(),
          200
        ).bind(this)
      );

      /*
       * Focus:
       * Empty input → initial content.
       * Existing term → run search.
       */
      this.input?.addEventListener(
        'focus',
        debounce(
          () => {
            const term =
              this.getQuery();

            if (!term.length) {
              this.showInitialPredictive();
              return;
            }

            this.onChange();
          },
          150
        ).bind(this)
      );

      /*
       * Header quick-search buttons.
       */
      this.button.forEach(
        (item) => {
          item.addEventListener(
            'click',
            (event) => {
              event.preventDefault();

              document.body
                .classList.add(
                  'open-cc'
                );

              if (
                this.is_drawer &&
                this.drawer
              ) {
                this.drawer
                  .classList.add(
                    'active'
                  );
              }

              return false;
            }
          );
        }
      );

      /*
       * Click capture closes overlay.
       */
      this.cc?.addEventListener(
        'click',
        this.close.bind(this)
      );

      /*
       * Escape closes search.
       */
      document.addEventListener(
        'keyup',
        (event) => {
          if (
            event.code &&
            event.code
              .toUpperCase() ===
              'ESCAPE'
          ) {
            this.close();
          }
        }
      );

      /*
       * Shield Recent Search links
       * from theme/global handlers.
       */
      if (
        !this._recentShieldBound
      ) {
        this._recentShieldBound =
          true;

        const isRecentTarget =
          (element) =>
            element?.closest?.(
              '.thb-predictive-search .thb-ps-recent-row .predictive-search__item.link.link--text, ' +
                '.thb-predictive-search .thb-ps-recent-row .thb-ps-recent-remove'
            );

        document.addEventListener(
          'pointerdown',
          (event) => {
            if (
              !document.body
                .classList.contains(
                  'open-search'
                )
            ) {
              return;
            }

            if (
              !isRecentTarget(
                event.target
              )
            ) {
              return;
            }

            event.stopPropagation();
          },
          true
        );

        document.addEventListener(
          'click',
          (event) => {
            if (
              !document.body
                .classList.contains(
                  'open-search'
                )
            ) {
              return;
            }

            if (
              !isRecentTarget(
                event.target
              )
            ) {
              return;
            }

            event.stopPropagation();

            /*
             * Do NOT preventDefault.
             * Links still navigate.
             */
          },
          true
        );
      }
    }


    getQuery() {
      return (
        this.input?.value || ''
      ).trim();
    }


    removeInertDeep(root) {
      if (!root) {
        return;
      }

      if (
        root.hasAttribute &&
        root.hasAttribute(
          'inert'
        )
      ) {
        root.removeAttribute(
          'inert'
        );
      }

      root
        .querySelectorAll?.(
          '[inert]'
        )
        ?.forEach(
          (element) =>
            element.removeAttribute(
              'inert'
            )
        );
    }


    /* ----------------------
     * GIBBERISH
     * ---------------------- */

    isGibberish(input) {
      const value =
        (input || '').trim();

      /*
       * Don't block short searches.
       */
      if (
        value.length < 12
      ) {
        return false;
      }

      /*
       * Allow phrases.
       */
      if (
        value.includes(' ')
      ) {
        return false;
      }

      /*
       * Allow SKU-style patterns.
       */
      if (
        /^[A-Z0-9]+(?:[-_][A-Z0-9]+)+$/i
          .test(value)
      ) {
        return false;
      }

      /*
       * Allow compact alphanumeric
       * part numbers.
       */
      if (
        /^(?=.*[A-Z])(?=.*\d)[A-Z0-9]{8,24}$/i
          .test(value)
      ) {
        return false;
      }

      /*
       * Allow numeric part numbers.
       */
      if (
        /^\d{5,}$/.test(value)
      ) {
        return false;
      }

      const letters =
        value.replace(
          /[^a-z]/gi,
          ''
        );

      if (
        letters.length >= 12
      ) {
        const vowels =
          (
            letters.match(
              /[aeiou]/gi
            ) || []
          ).length;

        const vowelRatio =
          vowels /
          letters.length;

        const hasLongConsonantRun =
          /[^aeiou]{6,}/i
            .test(letters);

        if (
          vowelRatio < 0.22 ||
          hasLongConsonantRun
        ) {
          return true;
        }
      }

      return false;
    }


    async getEmptyPredictiveMarkup() {
      if (
        this.emptyPredictiveMarkup
      ) {
        return (
          this.emptyPredictiveMarkup
        );
      }

      if (
        this.emptyPredictiveRequest
      ) {
        return (
          this.emptyPredictiveRequest
        );
      }

      this.emptyPredictiveRequest =
        fetch(
          '/?section_id=predictive-search',
          {
            credentials:
              'same-origin',

            headers: {
              'X-Requested-With':
                'XMLHttpRequest'
            }
          }
        )
          .then(
            (response) => {
              if (
                !response.ok
              ) {
                throw new Error(
                  `Empty predictive search failed: ${response.status}`
                );
              }

              return (
                response.text()
              );
            }
          )
          .then(
            (html) => {
              const documentFromResponse =
                new DOMParser()
                  .parseFromString(
                    html,
                    'text/html'
                  );

              const emptyState =
                documentFromResponse
                  .querySelector(
                    '#shopify-section-predictive-search [data-ps-empty]'
                  ) ||
                documentFromResponse
                  .querySelector(
                    '[data-ps-empty]'
                  );

              this.emptyPredictiveMarkup =
                emptyState
                  ?.outerHTML ||
                '';

              return (
                this
                  .emptyPredictiveMarkup
              );
            }
          )
          .catch(
            (error) => {
              console.error(
                'Unable to restore predictive empty state:',
                error
              );

              return '';
            }
          )
          .finally(
            () => {
              this.emptyPredictiveRequest =
                null;
            }
          );

      return (
        this.emptyPredictiveRequest
      );
    }


    async restoreEmptyPredictiveMarkup() {
      if (
        !this
          .predictiveSearchResults
      ) {
        return;
      }

      const existingEmptyState =
        this.predictiveSearchResults
          .querySelector(
            '[data-ps-empty]'
          );

      if (
        existingEmptyState
      ) {
        return;
      }

      const emptyMarkup =
        await this
          .getEmptyPredictiveMarkup();

      if (!emptyMarkup) {
        return;
      }

      /*
       * Customer started typing
       * while request was loading.
       */
      if (
        this.getQuery().length
      ) {
        return;
      }

      this.predictiveSearchResults
        .innerHTML =
        emptyMarkup;

      this.removeInertDeep(
        this.predictiveSearchResults
      );

      document.dispatchEvent(
        new CustomEvent(
          'predictive-search:empty-restored'
        )
      );
    }


    /* ----------------------
     * HIDE / SHOW
     * ---------------------- */

    async showInitialPredictive() {
      if (
        !this
          .predictiveSearchResults
      ) {
        return;
      }

      await this
        .restoreEmptyPredictiveMarkup();

      /*
       * Customer typed while
       * initial markup loaded.
       */
      if (
        this.getQuery().length
      ) {
        return;
      }

      this.predictiveSearchResults
        .removeAttribute(
          'inert'
        );

      this.predictiveSearchResults
        .classList.add(
          'active',
          'is-visible',
          'is-active',
          'open'
        );

      this.predictiveSearchResults
        .classList.remove(
          'loading',
          'has-no-match'
        );

      this.removeInertDeep(
        this.predictiveSearchResults
      );

      if (
        !this.is_drawer
      ) {
        document.body
          .classList.add(
            'open-search'
          );
      }

      if (
        this.is_drawer &&
        this.drawer
      ) {
        this.drawer
          .classList.remove(
            'showing-results'
          );
      }
    }


    hidePredictive({
      keepDrawerOpen = true
    } = {}) {
      /*
       * Abort in-flight HTML request.
       */
      if (
        this._fetchController
      ) {
        this._fetchController
          .abort();

        this._fetchController =
          null;
      }

      if (
        this
          .predictiveSearchResults
      ) {
        this.predictiveSearchResults
          .innerHTML = '';

        this.predictiveSearchResults
          .classList.remove(
            'active',
            'loading'
          );

        this.predictiveSearchResults
          .classList.remove(
            'has-no-match'
          );

        this.predictiveSearchResults
          .setAttribute(
            'inert',
            ''
          );
      }

      if (
        !this.is_drawer
      ) {
        document.body
          .classList.remove(
            'open-search'
          );
      }

      if (
        this.is_drawer &&
        this.drawer
      ) {
        this.drawer
          .classList.remove(
            'showing-results'
          );

        if (
          !keepDrawerOpen
        ) {
          this.drawer
            .classList.remove(
              'active'
            );
        }
      }
    }


    open() {
      document.body
        .classList.add(
          'open-search'
        );

      this.removeInertDeep(
        this.predictiveSearchResults
      );
    }


    close() {
      this.hidePredictive({
        keepDrawerOpen: false
      });
    }


    /* ----------------------
     * NO MATCH
     * ---------------------- */

    syncEmptyProductListState() {
      if (
        !this
          .predictiveSearchResults
      ) {
        return;
      }

      const productList =
        this.predictiveSearchResults
          .querySelector(
            '#thb-tab-products .thb-ps__list--products'
          );

      if (!productList) {
        return;
      }

      /*
       * Recommended Products can
       * contain product cards even
       * when the actual query matched
       * zero products.
       */
      const hasProducts =
        typeof
          this
            ._hasActualProductMatch ===
        'boolean'
          ? this
              ._hasActualProductMatch
          : !!productList
              .querySelector(
                '.product-card-small'
              );

      productList
        .classList.toggle(
          'empty-search-product-list',
          !hasProducts
        );
    }


    applyNoMatchState() {
      if (
        !this
          .predictiveSearchResults
      ) {
        return;
      }

      this.syncEmptyProductListState();

      const panels =
        this.predictiveSearchResults
          .querySelector(
            '.thb-ps-tab-panels'
          ) ||
        this.predictiveSearchResults
          .querySelector(
            '.thb-ps-tabs .thb-ps-tab-panels'
          );

      if (!panels) {
        return;
      }

      const hasProducts =
        panels.querySelector(
          '#thb-tab-products .thb-ps__group--products .product-card-small'
        );

      const hasArticles =
        panels.querySelector(
          '#thb-tab-articles .thb-ps__group--articles .thb-ps__article'
        );

      const hasCollections =
        panels.querySelector(
          '#thb-tab-products .thb-ps__group--collections .thb-ps__collection, #thb-tab-articles .thb-ps__group--collections .thb-ps__collection'
        );

      const hasAnyHTMLMatch =
        !!(
          hasProducts ||
          hasArticles ||
          hasCollections
        );

      panels.classList.toggle(
        'no-match',
        !hasAnyHTMLMatch
      );
    }


    /* ----------------------
     * MAIN FLOW
     * ---------------------- */

    async onChange() {
      const searchTerm =
        this.getQuery();

      if (
        this.is_drawer &&
        this.drawer
      ) {
        this.drawer
          .classList.toggle(
            'has-gibberish',
            this.isGibberish(
              searchTerm
            )
          );
      }

      /*
       * Gibberish:
       * don't fetch.
       */
      if (
        this.isGibberish(
          searchTerm
        )
      ) {
        this.hidePredictive({
          keepDrawerOpen: true
        });

        return;
      }

      /*
       * Empty input:
       * initial predictive content.
       */
      if (
        !searchTerm.length
      ) {
        this._lastTerm =
          '';

        await this
          .showInitialPredictive();

        return;
      }

      /*
       * Don't refetch same term.
       */
      if (
        searchTerm ===
        this._lastTerm
      ) {
        return;
      }

      this._lastTerm =
        searchTerm;

      if (
        !this.is_drawer
      ) {
        this.open();
      } else if (
        this.drawer
      ) {
        this.drawer
          .classList.add(
            'showing-results'
          );
      }

      this.removeInertDeep(
        this.predictiveSearchResults
      );

      this.predictiveSearchResults
        ?.classList.add(
          'active'
        );

      this.predictiveSearchResults
        ?.classList.remove(
          'has-no-match'
        );

      /*
       * Check compatibility first.
       */
      let xrefResult = {
        matches: [],
        productCount: null
      };

      const trimmedTerm =
        searchTerm.trim();

      const compactTerm =
        trimmedTerm.replace(
          /[^A-Z0-9]/gi,
          ''
        );

      const wordCount =
        trimmedTerm
          .split(/\s+/)
          .filter(Boolean)
          .length;

      const looksNumericPart =
        /\d/.test(
          trimmedTerm
        );

      const looksCodeLikePart =
        compactTerm.length >= 3 &&
        compactTerm.length <= 20;

      const looksTypePart =
        /\b(TYPE|SERIES|MODEL|SIZE)\b/i
          .test(trimmedTerm);

      const looksRomanPart =
        /\b[IVX]{1,6}\b/i
          .test(trimmedTerm);

      const isPlainWordsOnly =
        /^[a-z\s]+$/i
          .test(trimmedTerm);

      const looksLikePartSearch =
        trimmedTerm.length >= 3 &&
        trimmedTerm.length <= 40 &&
        wordCount <= 4 &&
        (
          looksNumericPart ||
          looksCodeLikePart ||
          looksTypePart ||
          looksRomanPart
        ) &&
        (
          !isPlainWordsOnly ||
          looksTypePart ||
          looksRomanPart
        );

      /*
       * Also allow short plain brand
       * searches such as:
       *
       * Aladdin
       * In The Swim
       */
      const shouldCheckCompatibility =
        looksLikePartSearch ||
        (
          trimmedTerm.length >= 3 &&
          trimmedTerm.length <= 40 &&
          wordCount <= 4
        );

      if (
        shouldCheckCompatibility
      ) {
        xrefResult =
          await this.fetchXref(
            searchTerm
          );
      }

      const xrefMatches =
        Array.isArray(
          xrefResult?.matches
        )
          ? xrefResult.matches
          : [];


      if (
        xrefMatches.length
      ) {
        await this
          .renderXrefProductResults(
            xrefMatches,
            searchTerm,
            xrefResult.productCount
          );

        return;
      }

      /*
       * Normal Shopify predictive
       * search fallback.
       */
      await this.getSearchResults(
        searchTerm
      );
    }


    onFormSubmit(event) {
      if (
        !this.getQuery().length
      ) {
        event.preventDefault();
      }
    }


    /* ----------------------
     * NORMAL SHOPIFY COUNTS
     * ---------------------- */

    async getPredictiveCounts(
      searchTerm
    ) {
      const url =
        `${theme.routes.predictive_search_url}.json?` +
        `q=${encodeURIComponent(searchTerm)}` +
        `&resources[type]=product,collection,article,page` +
        `&resources[limit]=10` +
        `&resources[limit_scope]=each` +
        `&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku`;

      const response =
        await fetch(
          url,
          {
            headers: {
              Accept:
                'application/json'
            }
          }
        );

      if (
        !response.ok
      ) {
        throw new Error(
          response.status
        );
      }

      const data =
        await response.json();

      const results =
        data?.resources
          ?.results || {};

      return {
        products:
          results.products
            ?.length || 0,

        articles:
          results.articles
            ?.length || 0,

        collections:
          results.collections
            ?.length || 0,

        pages:
          results.pages
            ?.length || 0
      };
    }


    async setNoMatchFromCounts(
      searchTerm
    ) {
      if (
        !this
          .predictiveSearchResults
      ) {
        return null;
      }

      const requestTerm =
        String(
          searchTerm || ''
        ).trim();

      try {
        const counts =
          await this
            .getPredictiveCounts(
              requestTerm
            );

        /*
         * Don't let a stale count
         * alter the current term.
         */
        if (
          this.getQuery() !==
          requestTerm
        ) {
          return null;
        }

        this._hasActualProductMatch =
          counts.products > 0;

        const hasAny =
          counts.products +
            counts.articles +
            counts.collections +
            counts.pages >
          0;

        this.predictiveSearchResults
          .classList.toggle(
            'has-no-match',
            !hasAny
          );

        this.syncEmptyProductListState();

        return counts;
      } catch (
        error
      ) {
        if (
          this.getQuery() !==
          requestTerm
        ) {
          return null;
        }

        console.warn(
          'Predictive counts failed:',
          error
        );

        /*
         * Unknown:
         * use DOM fallback.
         */
        this._hasActualProductMatch =
          null;

        return null;
      }
    }

    /* ----------------------
    * FULL PRODUCT COUNT
    * ---------------------- */

    async getFullProductCount(
      searchTerm
    ) {
      const term =
        String(
          searchTerm || ''
        ).trim();

      if (!term) {
        return null;
      }

      const key =
        term.toLowerCase();

      if (
        this._fullProductCountCache
          .has(key)
      ) {
        return (
          this._fullProductCountCache
            .get(key)
        );
      }

      try {
        if (
          this._fullProductCountController
        ) {
          this._fullProductCountController
            .abort();
        }

        this._fullProductCountController =
          new AbortController();

        const response =
          await fetch(
            `/search?${new URLSearchParams({
              q: term,
              type: 'product'
            }).toString()}`,
            {
              signal:
                this
                  ._fullProductCountController
                  .signal,

              headers: {
                'X-Requested-With':
                  'XMLHttpRequest'
              }
            }
          );

        if (
          !response.ok
        ) {
          throw new Error(
            response.status
          );
        }

        const html =
          await response.text();

        const doc =
          new DOMParser()
            .parseFromString(
              html,
              'text/html'
            );

        const countElement =
          doc.querySelector(
            '[data-search-total-count]'
          );

        if (!countElement) {
          return null;
        }

        const count =
          parseInt(
            countElement.dataset
              .searchTotalCount,
            10
          );

        if (
          !Number.isFinite(
            count
          )
        ) {
          return null;
        }

        this._fullProductCountCache
          .set(
            key,
            count
          );

        return count;

      } catch (
        error
      ) {
        if (
          error?.name ===
          'AbortError'
        ) {
          return null;
        }

        console.warn(
          'Full product count failed:',
          error
        );

        return null;
      }
    }


    /* ----------------------
    * NORMAL COUNT DISPLAY
    * ---------------------- */

    updateNormalPredictiveProductCount(
      searchTerm,
      totalCount
    ) {
      if (
        !this.predictiveSearchResults ||
        !Number.isFinite(
          totalCount
        )
      ) {
        return;
      }

      const productsGroup =
        this.predictiveSearchResults
          .querySelector(
            '#thb-tab-products .thb-ps__group--products'
          );

      if (!productsGroup) {
        return;
      }

      const heading =
        productsGroup.querySelector(
          '.thb-ps__heading'
        );

      if (heading) {
        heading.innerHTML =
          `Products for “${this.escapeHTML(searchTerm)}” ` +
          `<span class="thb-ps__count">(${totalCount})</span>`;
      }

      /*
      * Remove only the Products
      * tab's old Show All link.
      */
      this.predictiveSearchResults
        .querySelectorAll(
          '#thb-tab-products .thb-ps__seeall-wrap'
        )
        .forEach(
          element =>
            element.remove()
        );

      /*
      * No need for Show All
      * when 3 or fewer exist.
      */
      if (
        totalCount <= 3
      ) {
        return;
      }

      const rightCol =
        this.predictiveSearchResults
          .querySelector(
            '#thb-tab-products .thb-ps-col--right'
          );

      if (!rightCol) {
        return;
      }

      const seeAllWrap =
        document.createElement(
          'div'
        );

      seeAllWrap.className =
        'thb-ps__seeall-wrap';

      seeAllWrap.innerHTML = `
        <a
          class="full search-results-submit"
          href="/search?q=${encodeURIComponent(searchTerm)}&type=product"
        >
          Show All Results (${totalCount})
        </a>
      `;

      rightCol.appendChild(
        seeAllWrap
      );
    }

    /* ----------------------
     * NORMAL SHOPIFY SEARCH
     * ---------------------- */

    async getSearchResults(
      searchTerm
    ) {
      if (
        !this
          .predictiveSearchResults
      ) {
        return;
      }

      const requestTerm =
        String(
          searchTerm || ''
        ).trim();

      if (!requestTerm) {
        return;
      }

      /*
       * New normal Shopify query.
       */
      this._hasActualProductMatch =
        null;

      this.predictiveSearchResults
        .classList.add(
          'loading'
        );

      /*
      * Small predictive count is still
      * useful for no-match detection.
      */
      this.setNoMatchFromCounts(
        requestTerm
      );

      /*
      * Get the real Shopify search total
      * separately.
      *
      * This does NOT delay the preview
      * products from rendering.
      */
      const fullProductCountPromise =
        this.getFullProductCount(
          requestTerm
        );

      if (
        this._fetchController
      ) {
        this._fetchController
          .abort();
      }

      this._fetchController =
        new AbortController();

      const controller =
        this._fetchController;

      try {
        const response =
          await fetch(
            `${theme.routes.predictive_search_url}?q=${encodeURIComponent(requestTerm)}` +
            `&${encodeURIComponent('resources[type]')}=product,collection,article,query,page` +
            `&${encodeURIComponent('resources[limit]')}=10` +
            `&${encodeURIComponent('resources[limit_scope]')}=each` +
            `&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku` +
            `&section_id=predictive-search`,
            {
              signal:
                controller.signal
            }
          );

        if (
          !response.ok
        ) {
          throw new Error(
            response.status
          );
        }

        const responseText =
          await response.text();

        /*
         * Customer typed another
         * character while loading.
         */
        if (
          this.getQuery() !==
          requestTerm
        ) {
          return;
        }

        const doc =
          new DOMParser()
            .parseFromString(
              responseText,
              'text/html'
            );

        const section =
          doc.querySelector(
            '#shopify-section-predictive-search'
          );

        if (!section) {
          throw new Error(
            'Predictive search section missing'
          );
        }

        /*
         * Final stale-response check.
         */
        if (
          this.getQuery() !==
          requestTerm
        ) {
          return;
        }

        this.predictiveSearchResults
          .classList.remove(
            'loading'
          );

        this.renderSearchResults(
          section.innerHTML
        );

        /*
        * Preview is already rendered.
        * Now update the heading + Show All
        * with Shopify's actual full-search
        * product total.
        */
        const fullProductCount =
          await fullProductCountPromise;

        if (
          this.getQuery() !==
          requestTerm
        ) {
          return;
        }

        if (
          Number.isFinite(
            fullProductCount
          )
        ) {
          this.updateNormalPredictiveProductCount(
            requestTerm,
            fullProductCount
          );
        }
      } catch (
        error
      ) {
        if (
          error?.name ===
          'AbortError'
        ) {
          return;
        }

        if (
          this.getQuery() !==
          requestTerm
        ) {
          return;
        }

        console.error(
          'Predictive search error:',
          error
        );

        this.predictiveSearchResults
          .classList.remove(
            'loading'
          );
      }
    }


    /* ----------------------
     * XREF LOOKUP
     * ---------------------- */

    async fetchXref(term) {
      const key =
        (term || '')
          .trim()
          .toUpperCase();

      const emptyResult = {
        matches: [],
        productCount: null
      };

      if (!key) {
        return emptyResult;
      }


      if (
        this._xrefCache.has(
          key
        )
      ) {
        return (
          this._xrefCache.get(
            key
          )
        );
      }


      try {
        if (
          this._xrefController
        ) {
          this._xrefController
            .abort();
        }


        this._xrefController =
          new AbortController();


        const response =
          await fetch(
            `https://compatibilitytool.julian-aquasure.workers.dev/xref?q=${encodeURIComponent(term)}`,
            {
              signal:
                this
                  ._xrefController
                  .signal
            }
          );


        if (
          !response.ok
        ) {
          return emptyResult;
        }


        const data =
          await response.json();


        const result = {
          matches:
            Array.isArray(
              data.matches
            )
              ? data.matches
              : [],

          productCount:
            Number.isFinite(
              data.product_count
            )
              ? data.product_count
              : null
        };


        this._xrefCache.set(
          key,
          result
        );


        return result;

      } catch (
        error
      ) {

        if (
          error?.name ===
          'AbortError'
        ) {
          return emptyResult;
        }


        console.warn(
          'xref fetch failed',
          error
        );


        return emptyResult;
      }
    }


    /* ----------------------
     * XREF PRODUCT RESULTS
     * ---------------------- */

    async renderXrefProductResults(
      matches,
      originalTerm,
      workerProductCount = null
    ) {
      if (
        !this
          .predictiveSearchResults
      ) {
        return;
      }

      const exactTotalCount =
        Number.isFinite(
          workerProductCount
        )
          ? workerProductCount
          : null;

    
      /*
      * When the Worker supplied an exact
      * product count, prioritize only matches
      * that the Worker actually resolved to
      * Shopify products.
      *
      * This avoids wasting predictive-search
      * requests on compatibility SKUs that
      * don't currently have a storefront
      * product.
      */
      const resolvedMatches =
        exactTotalCount !== null
          ? (matches || []).filter(
              (match) =>
                match?.product_id ||
                match?.product_handle
            )
          : (matches || []);


      /*
      * Keep original fallback behavior if
      * the Worker didn't supply product
      * mappings for some reason.
      */
      const sourceMatches =
        resolvedMatches.length
          ? resolvedMatches
          : (matches || []);


      const skus = [
        ...new Set(
          sourceMatches
            .map(
              (match) =>
                String(
                  match
                    ?.clarus_sku ||
                    ''
                )
                  .trim()
                  .toUpperCase()
            )
            .filter(Boolean)
        )
      ];

      if (
        !skus.length
      ) {
        await this.getSearchResults(
          originalTerm
        );

        return;
      }

      /*
       * Unique render token prevents
       * old XREF searches from updating
       * a newer search term.
       */
      const renderToken =
        Symbol(
          'xref-render'
        );

      this._xrefRenderToken =
        renderToken;

      if (
        this._fetchController
      ) {
        this._fetchController
          .abort();
      }

      this._fetchController =
        new AbortController();

      const signal =
        this
          ._fetchController
          .signal;

      this.predictiveSearchResults
        .classList.add(
          'loading'
        );

      this.predictiveSearchResults
        .classList.remove(
          'has-no-match'
        );

      /*
       * Store by Shopify parent
       * product URL so variants don't
       * inflate the count.
       */
      const uniqueProducts =
        new Map();

      let shellDoc =
        null;

      let firstRenderDone =
        false;

      /*
       * Reuse a previously loaded
       * predictive-search shell.
       */
      if (
        this._xrefShellHtml
      ) {
        shellDoc =
          new DOMParser()
            .parseFromString(
              this._xrefShellHtml,
              'text/html'
            );
      }

      /*
       * Get a stable key for the
       * Shopify parent product.
       */
      const getProductKey =
        (card) => {
          const productLink =
            card.querySelector(
              'a[href*="/products/"]'
            );

          const href =
            productLink
              ?.getAttribute(
                'href'
              );

          if (href) {
            try {
              const url =
                new URL(
                  href,
                  window.location
                    .origin
                );

              /*
               * pathname removes
               * ?variant=...
               */
              return (
                url.pathname
                  .toLowerCase()
              );
            } catch (
              error
            ) {}
          }

          const productId =
            card.dataset
              .productId ||
            card
              .querySelector(
                '[data-product-id]'
              )
              ?.getAttribute(
                'data-product-id'
              );

          if (
            productId
          ) {
            return (
              `product:${productId}`
            );
          }

          return (
            card.textContent ||
            ''
          )
            .trim()
            .toLowerCase();
        };


      /*
       * Resolve one compatible SKU
       * to one Shopify parent card.
       */
      const resolveSku =
        async (sku) => {
          if (
            this
              ._xrefProductCache
              .has(sku)
          ) {
            return (
              this
                ._xrefProductCache
                .get(sku)
            );
          }

          try {
            const response =
              await fetch(
                `${theme.routes.predictive_search_url}?q=${encodeURIComponent(sku)}` +
                  `&${encodeURIComponent('resources[type]')}=product,collection,article,query,page` +
                  `&${encodeURIComponent('resources[limit]')}=10` +
                  `&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku` +
                  `&section_id=predictive-search`,
                {
                  signal
                }
              );

            if (
              !response.ok
            ) {
              this
                ._xrefProductCache
                .set(
                  sku,
                  null
                );

              return null;
            }

            const responseText =
              await response.text();

            const doc =
              new DOMParser()
                .parseFromString(
                  responseText,
                  'text/html'
                );

            const section =
              doc.querySelector(
                '#shopify-section-predictive-search'
              );

            if (!section) {
              this
                ._xrefProductCache
                .set(
                  sku,
                  null
                );

              return null;
            }

            /*
             * Keep reusable shell.
             */
            if (
              !this
                ._xrefShellHtml
            ) {
              this
                ._xrefShellHtml =
                section.outerHTML;
            }

            if (!shellDoc) {
              shellDoc =
                doc;
            }

            const cards =
              section
                .querySelectorAll(
                  '#thb-tab-products .thb-ps__group--products .product-card-small'
                );

            let resolved =
              null;

            for (
              const card
              of cards
            ) {
              const cardHtml =
                card.outerHTML;

              const cardText =
                (
                  card.textContent ||
                  ''
                ).toUpperCase();

              const upperHtml =
                cardHtml
                  .toUpperCase();

              /*
               * Make sure Shopify's
               * returned card actually
               * includes this SKU.
               */
              if (
                !cardText.includes(
                  sku
                ) &&
                !upperHtml.includes(
                  sku
                )
              ) {
                continue;
              }

              const productKey =
                getProductKey(
                  card
                );

              if (
                !productKey
              ) {
                continue;
              }

              resolved = {
                productKey,
                cardHtml,
                sku
              };

              console.log(
                'XREF RESOLVED:',
                sku,
                productKey
              );

              break;
            }

            this
              ._xrefProductCache
              .set(
                sku,
                resolved
              );

            return resolved;
          } catch (
            error
          ) {
            if (
              error?.name ===
              'AbortError'
            ) {
              throw error;
            }

            console.warn(
              'XREF SKU product lookup failed:',
              sku,
              error
            );

            /*
             * Don't permanently cache
             * a network failure.
             */
            return null;
          }
        };


      /*
       * Render first three unique
       * products as soon as we have
       * enough.
       */
      const renderFirstProducts =
        () => {
          if (
            firstRenderDone ||
            uniqueProducts.size <
              3 ||
            !shellDoc ||
            this
              ._xrefRenderToken !==
              renderToken
          ) {
            return;
          }

          const cards =
            Array.from(
              uniqueProducts
                .values()
            )
              .slice(
                0,
                3
              )
              .map(
                (product) =>
                  product
                    .cardHtml
              );

          this.renderXrefMarkup({
            shellDoc,

            productCardsHtml:
              cards.join(''),

            originalTerm,

            shownCount:
              cards.length,

            /*
             * Exact total is not
             * known yet.
             */
            totalCount:
              exactTotalCount
          });

          this.predictiveSearchResults
            .classList.remove(
              'loading'
            );

          firstRenderDone =
            true;
        };


      /*
       * Resolve several SKUs
       * at a time.
       */
      const BATCH_SIZE =
        5;

      try {
        for (
          let start = 0;
          start < skus.length;
          start += BATCH_SIZE
        ) {
          if (
            this
              ._xrefRenderToken !==
              renderToken
          ) {
            return;
          }

          const batch =
            skus.slice(
              start,
              start +
                BATCH_SIZE
            );

          const resolvedProducts =
            await Promise.all(
              batch.map(
                (sku) =>
                  resolveSku(
                    sku
                  )
              )
            );

          resolvedProducts
            .forEach(
              (product) => {
                if (
                  !product
                ) {
                  return;
                }

                if (
                  uniqueProducts
                    .has(
                      product
                        .productKey
                    )
                ) {
                  return;
                }

                uniqueProducts
                  .set(
                    product
                      .productKey,
                    product
                  );
              }
            );

          /*
           * Display first three
           * as soon as possible.
           */
          renderFirstProducts();

          if (
            exactTotalCount !== null &&
            uniqueProducts.size >=
              Math.min(
                3,
                exactTotalCount
              )
          ) {
            break;
          }
        }


        /*
         * Everything finished.
         * Exact unique parent-product
         * count is now known.
         */
        if (
          this
            ._xrefRenderToken !==
            renderToken
        ) {
          return;
        }

        const products =
          Array.from(
            uniqueProducts
              .values()
          );

        if (
          !products.length ||
          !shellDoc
        ) {
          await this
            .getSearchResults(
              originalTerm
            );

          return;
        }

        const displayedProducts =
          products.slice(
            0,
            3
          );

        const totalCount =
          exactTotalCount !== null
            ? exactTotalCount
            : products.length;

        /*
         * If fewer than 3 exist,
         * early render never fired.
         */
        if (
          !firstRenderDone
        ) {
          this.renderXrefMarkup({
            shellDoc,

            productCardsHtml:
              displayedProducts
                .map(
                  (product) =>
                    product
                      .cardHtml
                )
                .join(''),

            originalTerm,

            shownCount:
              displayedProducts
                .length,

            totalCount
          });
        } else {
          /*
           * Cards are already visible.
           * Just add final count and
           * Show All when appropriate.
           */
          this.updateXrefFinalCount(
            originalTerm,
            totalCount
          );
        }

        this.predictiveSearchResults
          .classList.remove(
            'loading'
          );
      } catch (
        error
      ) {
        if (
          error?.name ===
          'AbortError'
        ) {
          return;
        }

        console.error(
          'XREF product render error:',
          error
        );

        /*
         * Preserve normal Shopify
         * fallback.
         */
        await this.getSearchResults(
          originalTerm
        );
      }
    }


    /* ----------------------
     * XREF FINAL COUNT
     * ---------------------- */

    updateXrefFinalCount(
      originalTerm,
      totalCount
    ) {
      if (
        !this
          .predictiveSearchResults
      ) {
        return;
      }

      const productsGroup =
        this.predictiveSearchResults
          .querySelector(
            '#thb-tab-products .thb-ps__group--products'
          );

      if (
        !productsGroup
      ) {
        return;
      }

      const heading =
        productsGroup
          .querySelector(
            '.thb-ps__heading'
          );

      const hasPartNumber =
        /\d/.test(
          originalTerm
        );

      const headingLabel =
        hasPartNumber
          ? 'Compatible products for'
          : 'Products for';

      if (heading) {
        const countMarkup =
          Number.isFinite(
            totalCount
          )
            ? ` <span class="thb-ps__count">(${totalCount})</span>`
            : '';

        heading.innerHTML =
          `${headingLabel} “${this.escapeHTML(originalTerm)}”${countMarkup}`;
      }

      /*
       * Remove old Show All.
       */
      this.predictiveSearchResults
        .querySelectorAll(
          '.thb-ps__seeall-wrap'
        )
        .forEach(
          (element) =>
            element.remove()
        );

      /*
       * 1–3 results:
       * no Show All.
       */
      if (
        totalCount <= 3
      ) {
        return;
      }

      const rightCol =
        this.predictiveSearchResults
          .querySelector(
            '#thb-tab-products .thb-ps-col--right'
          );

      if (!rightCol) {
        return;
      }

      const seeAllWrap =
        document.createElement(
          'div'
        );

      seeAllWrap.className =
        'thb-ps__seeall-wrap';

      seeAllWrap.innerHTML = `
        <a
          class="full search-results-submit"
          href="/search?q=${encodeURIComponent(originalTerm)}&type=product"
        >
          Show All Results (${totalCount})
        </a>
      `;

      rightCol.appendChild(
        seeAllWrap
      );
    }


    /* ----------------------
     * XREF MARKUP
     * ---------------------- */

    renderXrefMarkup({
      shellDoc,
      productCardsHtml,
      originalTerm,
      shownCount,
      totalCount
    }) {
      if (
        !this
          .predictiveSearchResults ||
        !shellDoc
      ) {
        return;
      }

      const section =
        shellDoc.querySelector(
          '#shopify-section-predictive-search'
        );

      if (!section) {
        return;
      }

      const wrapper =
        document.createElement(
          'div'
        );

      wrapper.innerHTML =
        section.innerHTML;

      const productsPanel =
        wrapper.querySelector(
          '#thb-tab-products'
        );

      const productsGroup =
        wrapper.querySelector(
          '#thb-tab-products .thb-ps__group--products'
        );

      const productsList =
        wrapper.querySelector(
          '#thb-tab-products .thb-ps__group--products .thb-ps__list--products'
        );

      if (
        !productsPanel ||
        !productsGroup ||
        !productsList
      ) {
        /*
         * Theme markup changed:
         * use returned Shopify markup
         * rather than failing.
         */
        this.predictiveSearchResults
          .innerHTML =
          section.innerHTML;

        this.predictiveSearchResults
          .classList.remove(
            'has-no-match'
          );

        this.removeInertDeep(
          this.predictiveSearchResults
        );

        return;
      }

      /*
       * Force Products tab active.
       */
      wrapper
        .querySelectorAll(
          '.thb-ps-tab-btn'
        )
        .forEach(
          (button) =>
            button.classList
              .remove(
                'is-active'
              )
        );

      wrapper
        .querySelectorAll(
          '.thb-ps-tab-panel'
        )
        .forEach(
          (panel) =>
            panel.classList
              .remove(
                'is-active'
              )
        );

      const productsTabBtn =
        wrapper.querySelector(
          '.thb-ps-tab-btn[data-tab="products"]'
        );

      if (
        productsTabBtn
      ) {
        productsTabBtn
          .classList.add(
            'is-active'
          );
      }

      productsPanel
        .classList.add(
          'is-active'
        );

      /*
       * Heading.
       */
      const heading =
        productsGroup
          .querySelector(
            '.thb-ps__heading'
          );

      const hasPartNumber =
        /\d/.test(
          originalTerm
        );

      const headingLabel =
        hasPartNumber
          ? 'Compatible products for'
          : 'Products for';

      if (heading) {
        /*
         * During first progressive
         * render totalCount is null,
         * so do not show an incorrect
         * count.
         */
        const countMarkup =
          Number.isFinite(
            totalCount
          )
            ? ` <span class="thb-ps__count">(${totalCount})</span>`
            : '';

        heading.innerHTML =
          `${headingLabel} “${this.escapeHTML(originalTerm)}”${countMarkup}`;
      }

      /*
       * XREF returned genuine
       * compatible products.
       */
      this._hasActualProductMatch =
        true;

      /*
       * Replace only product list.
       */
      productsList.innerHTML =
        productCardsHtml;

      /*
       * XREF product list should only
       * be marked empty when it truly
       * contains no product card.
       */
      productsList
        .classList.toggle(
          'empty-search-product-list',
          !productsList
            .querySelector(
              '.product-card-small'
            )
        );

      /*
       * Remove old Show All.
       */
      wrapper
        .querySelectorAll(
          '.thb-ps__seeall-wrap'
        )
        .forEach(
          (element) =>
            element.remove()
        );

      /*
       * Exact count must be known
       * before we decide whether
       * there are more than 3.
       */
      if (
        Number.isFinite(
          totalCount
        ) &&
        totalCount > 3
      ) {
        const seeAllWrap =
          document.createElement(
            'div'
          );

        seeAllWrap.className =
          'thb-ps__seeall-wrap';

        seeAllWrap.innerHTML = `
          <a
            class="full search-results-submit"
            href="/search?q=${encodeURIComponent(originalTerm)}&type=product"
          >
            Show All Results (${totalCount})
          </a>
        `;

        const rightCol =
          wrapper.querySelector(
            '#thb-tab-products .thb-ps-col--right'
          );

        if (
          rightCol
        ) {
          rightCol.appendChild(
            seeAllWrap
          );
        }
      }

      this.predictiveSearchResults
        .innerHTML =
        wrapper.innerHTML;

      this.predictiveSearchResults
        .classList.remove(
          'has-no-match'
        );

      this.removeInertDeep(
        this.predictiveSearchResults
      );
    }


    /* ----------------------
     * EXACT SKU PRIORITY
     * ---------------------- */

    prioritizeExactSkuCards(
      term
    ) {
      if (
        !this
          .predictiveSearchResults ||
        !term
      ) {
        return;
      }

      const normalizedTerm =
        String(term)
          .trim()
          .toUpperCase()
          .replace(
            /[\s_]+/g,
            '-'
          )
          .replace(
            /-+/g,
            '-'
          );

      if (
        !normalizedTerm
      ) {
        return;
      }

      const productList =
        this.predictiveSearchResults
          .querySelector(
            '#thb-tab-products .thb-ps__group--products .thb-ps__list--products'
          );

      if (
        !productList
      ) {
        return;
      }

      const cards =
        Array.from(
          productList
            .querySelectorAll(
              '.product-card-small'
            )
        );

      if (
        !cards.length
      ) {
        return;
      }

      const scoreCard =
        (card) => {
          const text =
            (
              card.textContent ||
              ''
            ).toUpperCase();

          const html =
            (
              card.innerHTML ||
              ''
            ).toUpperCase();

          const exactSku =
            text.includes(
              normalizedTerm
            ) ||
            html.includes(
              normalizedTerm
            );

          const titleStartsWithSku =
            text.includes(
              `\n${normalizedTerm}\n`
            ) ||
            text
              .trim()
              .startsWith(
                normalizedTerm
              );

          const packPenalty =
            /-2PK|-4PK|-6PK|-8PK|-12PK/
              .test(text) ||
            /PACK/
              .test(text);

          if (
            exactSku &&
            !packPenalty
          ) {
            return 100;
          }

          if (
            exactSku &&
            titleStartsWithSku
          ) {
            return 95;
          }

          if (
            exactSku
          ) {
            return 80;
          }

          return 0;
        };

      const sorted =
        [...cards]
          .sort(
            (a, b) =>
              scoreCard(b) -
              scoreCard(a)
          );

      const changed =
        sorted.some(
          (card, index) =>
            card !==
            cards[index]
        );

      if (!changed) {
        return;
      }

      sorted.forEach(
        (card) =>
          productList
            .appendChild(
              card
            )
      );
    }


    /* ----------------------
     * NORMAL RESULT RENDER
     * ---------------------- */

    renderSearchResults(
      markup
    ) {
      if (
        !this
          .predictiveSearchResults
      ) {
        return;
      }

      this.removeInertDeep(
        this.predictiveSearchResults
      );

      this.predictiveSearchResults
        .innerHTML =
        markup;

      /*
       * Apply actual-match state.
       * This is what makes
       * Recommended Products receive
       * empty-search-product-list when
       * the entered term matched zero.
       */
      this.syncEmptyProductListState();

      this.removeInertDeep(
        this.predictiveSearchResults
      );

      requestAnimationFrame(
        () =>
          this.removeInertDeep(
            this
              .predictiveSearchResults
          )
      );

      if (
        typeof
          this
            .highlightSearchTerms ===
        'function'
      ) {
        this.highlightSearchTerms(
          this.getQuery()
        );
      }

      this.prioritizeExactSkuCards(
        this.getQuery()
      );

      this.applyNoMatchState();

      requestAnimationFrame(
        () => {
          this.applyNoMatchState();

          /*
           * Keep the wrapper usable
           * whenever returned markup
           * contains a valid content
           * group.
           */
          const hasProducts =
            this.predictiveSearchResults
              .querySelector(
                '#thb-tab-products .thb-ps__group--products .product-card-small'
              );

          const hasArticles =
            this.predictiveSearchResults
              .querySelector(
                '#thb-tab-articles .thb-ps__group--articles .thb-ps__article'
              );

          const hasCollections =
            this.predictiveSearchResults
              .querySelector(
                '#thb-tab-products .thb-ps__group--collections .thb-ps__collection, #thb-tab-articles .thb-ps__group--collections .thb-ps__collection'
              );

          if (
            hasProducts ||
            hasArticles ||
            hasCollections
          ) {
            this.predictiveSearchResults
              .classList.remove(
                'has-no-match'
              );
          }

          /*
           * Re-apply empty product
           * class after any theme
           * DOM changes.
           */
          this.syncEmptyProductListState();
        }
      );

      if (
        !this.is_drawer
      ) {
        const button =
          this.predictiveSearchResults
            .querySelector(
              '#search-results-submit'
            );

        if (button) {
          button.classList
            .remove(
              'button'
            );
        }
      }
    }


    /* ----------------------
     * SEARCH TERM HIGHLIGHT
     * ---------------------- */

    escapeHTML(str) {
      const div =
        document.createElement(
          'div'
        );

      div.textContent =
        str;

      return div.innerHTML;
    }


    applyHighlightToElements(
      elements,
      term
    ) {
      if (
        !elements ||
        !elements.length
      ) {
        return;
      }

      const query =
        (term || '')
          .trim()
          .toLowerCase();

      elements.forEach(
        (element) => {
          const raw =
            element.getAttribute(
              'data-label'
            ) ||
            element.textContent ||
            '';

          if (!query) {
            element.innerHTML =
              this.escapeHTML(
                raw
              );

            return;
          }

          const index =
            raw
              .toLowerCase()
              .indexOf(
                query
              );

          if (
            index === -1
          ) {
            element.innerHTML =
              this.escapeHTML(
                raw
              );

            return;
          }

          const before =
            this.escapeHTML(
              raw.slice(
                0,
                index
              )
            );

          const typed =
            this.escapeHTML(
              raw.slice(
                index,
                index +
                  query.length
              )
            );

          const after =
            this.escapeHTML(
              raw.slice(
                index +
                  query.length
              )
            );

          if (
            index === 0
          ) {
            element.innerHTML =
              `<span class="ps-typed">${typed}</span><strong class="ps-untyped">${after}</strong>`;
          } else {
            element.innerHTML =
              `<strong class="ps-untyped">${before}</strong><span class="ps-typed">${typed}</span><strong class="ps-untyped">${after}</strong>`;
          }
        }
      );
    }


    highlightSearchTerms(
      term
    ) {
      const metafieldAnchors =
        this.predictiveSearchResults
          ?.querySelectorAll(
            '.thb-ps__group--queries a[data-source="metafield"]'
          );

      const categoryTitles =
        this.predictiveSearchResults
          ?.querySelectorAll(
            '.thb-ps__group--collections .thb-ps__title[data-label]'
          );

      this.applyHighlightToElements(
        metafieldAnchors,
        term
      );

      this.applyHighlightToElements(
        categoryTitles,
        term
      );
    }
  }


  customElements.define(
    'search-form',
    SearchForm
  );


  /*
   * Predictive Search tab switching.
   * Works in header and drawer.
   */
  document.addEventListener(
    'click',
    (event) => {
      const button =
        event.target.closest(
          '.thb-ps-tab-btn'
        );

      if (!button) {
        return;
      }

      event.preventDefault();

      const tabsContainer =
        button.closest(
          '.thb-ps-tabs'
        );

      if (
        !tabsContainer
      ) {
        return;
      }

      const tabs =
        tabsContainer
          .querySelectorAll(
            '.thb-ps-tab-btn'
          );

      const panels =
        tabsContainer
          .querySelectorAll(
            '.thb-ps-tab-panel'
          );

      tabs.forEach(
        (tab) =>
          tab.classList
            .remove(
              'is-active'
            )
      );

      panels.forEach(
        (panel) =>
          panel.classList
            .remove(
              'is-active'
            )
      );

      button.classList.add(
        'is-active'
      );

      const target =
        tabsContainer
          .querySelector(
            `#thb-tab-${button.dataset.tab}`
          );

      if (target) {
        target.classList.add(
          'is-active'
        );
      }
    }
  );
}